import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reviewApi, type ReviewVoteValue } from '../api/client';

interface ReviewersPopoverProps {
  path: string;
}

const STATUS_LABEL: Record<ReviewVoteValue, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  no_response: 'No response',
};

const STATUS_BADGE_CLASS: Record<ReviewVoteValue, string> = {
  draft: 'border-wc-muted/40 bg-wc-gray text-wc-muted-strong',
  in_review: 'border-amber-300 bg-amber-50 text-amber-800',
  approved: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  no_response: 'border-wc-muted/30 bg-transparent text-wc-muted',
};

const STATUS_DOT_CLASS: Record<ReviewVoteValue, string> = {
  draft: 'bg-wc-muted',
  in_review: 'bg-amber-500',
  approved: 'bg-emerald-500',
  no_response: 'bg-wc-muted/40',
};

/**
 * Toolbar button + popover that lists every reviewer for the calling
 * customer and their current vote on this document (or "No response" if
 * they have not voted). Visible to every authenticated portal user, not
 * just reviewers, so any viewer can see at a glance who has approved the
 * document they are looking at -- mirroring the GitHub PR reviewers panel.
 *
 * Shares the `['review-status', path]` query key with `DocumentStatusControl`
 * so opening the popover does not trigger a second roundtrip when the
 * status pill already loaded the payload.
 */
export function ReviewersPopover({ path }: ReviewersPopoverProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(event: MouseEvent): void {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target as Node)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const query = useQuery({
    queryKey: ['review-status', path],
    queryFn: () => reviewApi.getStatus(path),
    enabled: !!path,
    staleTime: 15_000,
  });

  // Excluded files have no review workflow attached -- hide the popover
  // entirely so the toolbar matches the (empty) state on excluded files.
  if (query.data?.reviewable === false) {
    return null;
  }
  const reviewers = query.data?.reviewers ?? [];
  const approvedCount = reviewers.filter((r) => r.status === 'approved').length;
  const totalCount = reviewers.length;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1.5 rounded-md border border-wc-muted/30 px-3 py-1.5 text-xs font-medium text-wc-muted-strong transition-colors hover:border-wc-blue hover:text-wc-blue"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="View reviewers and their statuses"
      >
        <UsersIcon />
        Reviewers ({approvedCount}/{totalCount})
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Reviewers"
          className="absolute right-0 top-full z-20 mt-2 w-96 max-w-[90vw] overflow-hidden rounded-lg border border-wc-muted/20 bg-wc-white shadow-xl"
        >
          <div className="border-b border-wc-muted/15 bg-wc-gray/60 px-4 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-wc-muted-strong">
              Reviewers
            </p>
            <p className="mt-0.5 truncate text-xs text-wc-black" title={path}>
              {path}
            </p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {query.isLoading ? (
              <p className="px-4 py-6 text-center text-xs text-wc-muted">Loading…</p>
            ) : query.isError ? (
              <p className="px-4 py-6 text-center text-xs text-wc-red">
                Failed to load reviewers.
              </p>
            ) : reviewers.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-wc-muted">
                No reviewers have been assigned for this customer yet.
              </p>
            ) : (
              <ul className="divide-y divide-wc-muted/15">
                {reviewers.map((r) => (
                  <li
                    key={r.portalUserId}
                    className="flex items-center gap-3 px-4 py-2.5 text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-wc-black">
                        {r.displayName ?? r.email}
                      </p>
                      {r.displayName && (
                        <p className="truncate text-wc-muted-strong">{r.email}</p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-medium ${STATUS_BADGE_CLASS[r.status]}`}
                    >
                      <span
                        aria-hidden
                        className={`h-2 w-2 rounded-full ${STATUS_DOT_CLASS[r.status]}`}
                      />
                      {STATUS_LABEL[r.status]}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UsersIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
